import { useState, useEffect, useMemo } from 'react'
import { Search, X, Check, Link2, Unlink } from 'lucide-react'
import { materialService, type Material } from '@/services/materials'

interface MaterialMappingModalProps {
  isOpen: boolean
  hardwareName: string
  hardwareSpec?: string
  currentMaterialId?: string | null
  currentMaterialCode?: string | null
  currentMaterialName?: string | null
  onSelect: (material: Material) => void
  onUnmap?: () => void
  onClose: () => void
}

export function MaterialMappingModal({
  isOpen,
  hardwareName,
  hardwareSpec,
  currentMaterialId,
  currentMaterialCode,
  currentMaterialName,
  onSelect,
  onUnmap,
  onClose,
}: MaterialMappingModalProps) {
  const [materials, setMaterials] = useState<Material[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [groupFilter, setGroupFilter] = useState<'all' | 'nk' | 'other'>('all')

  useEffect(() => {
    if (!isOpen) return
    let active = true
    setLoading(true)
    materialService
      .list()
      .then((data) => {
        if (active) {
          setMaterials(data || [])
          setLoading(false)
        }
      })
      .catch((err) => {
        console.error('Lỗi khi tải danh sách vật tư:', err)
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [isOpen])

  // Pre-fill search term if empty with hardware name or spec
  useEffect(() => {
    if (isOpen && !searchTerm && hardwareName) {
      setSearchTerm(hardwareName.trim().slice(0, 20))
    }
  }, [isOpen, hardwareName])

  const filteredMaterials = useMemo(() => {
    const term = searchTerm.toLowerCase().trim()
    return materials.filter((m) => {
      // Group filter
      if (groupFilter === 'nk') {
        const grp = (m.group_code || '').toLowerCase()
        if (grp !== 'nk' && grp !== 'ngu_kim' && grp !== 'hardware') return false
      } else if (groupFilter === 'other') {
        const grp = (m.group_code || '').toLowerCase()
        if (grp === 'nk' || grp === 'ngu_kim' || grp === 'hardware') return false
      }

      if (!term) return true
      return (
        (m.code || '').toLowerCase().includes(term) ||
        (m.name || '').toLowerCase().includes(term) ||
        (m.uom_default || '').toLowerCase().includes(term)
      )
    })
  }, [materials, searchTerm, groupFilter])

  if (!isOpen) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="material-mapping-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl overflow-hidden border border-slate-300">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3.5 bg-slate-50">
          <div>
            <h3 id="material-mapping-title" className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <Link2 className="h-4 w-4 text-blue-600" />
              Ánh Xạ Vật Tư Ngũ Kim Vào ERP
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Chọn vật tư thật trong hệ thống để tự động đồng bộ đơn giá và nhà cung cấp.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700 transition"
            aria-label="Đóng"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* BOM Line Context */}
        <div className="bg-blue-50/60 border-b border-blue-100 px-5 py-2.5 text-xs">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-blue-900">Dòng ngũ kim:</span>
            <span className="font-bold text-slate-800">{hardwareName}</span>
            {hardwareSpec && <span className="text-slate-500">({hardwareSpec})</span>}
          </div>
          {currentMaterialId && (
            <div className="mt-1 flex items-center gap-2 text-[11px] text-emerald-800">
              <span className="font-medium">Đang ánh xạ với:</span>
              <span className="font-mono font-bold bg-emerald-100 px-1.5 py-0.5 rounded border border-emerald-300">
                {currentMaterialCode || currentMaterialId}
              </span>
              <span>{currentMaterialName}</span>
              {onUnmap && (
                <button
                  type="button"
                  onClick={onUnmap}
                  className="ml-auto inline-flex items-center gap-1 text-rose-600 hover:text-rose-800 font-semibold underline cursor-pointer"
                >
                  <Unlink className="h-3 w-3" />
                  Hủy ánh xạ
                </button>
              )}
            </div>
          )}
        </div>

        {/* Search & Filter Toolbar */}
        <div className="p-4 border-b border-slate-200 space-y-3 bg-white">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Tìm theo mã hoặc tên vật tư..."
              className="w-full pl-9 pr-8 py-1.5 text-xs rounded border border-slate-300 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              autoFocus
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-2.5 text-slate-400 hover:text-slate-600"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 text-xs">
            <span className="text-slate-500 font-medium">Nhóm:</span>
            <button
              type="button"
              onClick={() => setGroupFilter('all')}
              className={`px-2.5 py-1 rounded text-xs font-semibold transition ${
                groupFilter === 'all'
                  ? 'bg-slate-800 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Tất cả ({materials.length})
            </button>
            <button
              type="button"
              onClick={() => setGroupFilter('nk')}
              className={`px-2.5 py-1 rounded text-xs font-semibold transition ${
                groupFilter === 'nk'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Ngũ kim (NK)
            </button>
            <button
              type="button"
              onClick={() => setGroupFilter('other')}
              className={`px-2.5 py-1 rounded text-xs font-semibold transition ${
                groupFilter === 'other'
                  ? 'bg-slate-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              Nhóm khác
            </button>
          </div>
        </div>

        {/* Materials List Table */}
        <div className="flex-1 overflow-y-auto p-4 min-h-[240px]">
          {loading ? (
            <div className="py-12 text-center text-xs text-slate-400">Đang tải danh sách vật tư...</div>
          ) : filteredMaterials.length === 0 ? (
            <div className="py-12 text-center text-xs text-slate-400">
              Không tìm thấy vật tư nào phù hợp với từ khóa &quot;{searchTerm}&quot;.
            </div>
          ) : (
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold text-slate-600 uppercase">
                  <th className="py-2 px-3 w-28">Mã vật tư</th>
                  <th className="py-2 px-3">Tên vật tư</th>
                  <th className="py-2 px-3 w-16 text-center">ĐVT</th>
                  <th className="py-2 px-3 w-24 text-center">Nhóm</th>
                  <th className="py-2 px-3 w-20 text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredMaterials.map((m) => {
                  const isSelected = m.id === currentMaterialId
                  return (
                    <tr
                      key={m.id}
                      onClick={() => onSelect(m)}
                      className={`cursor-pointer transition-colors ${
                        isSelected ? 'bg-blue-50/80' : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="py-2 px-3 font-mono font-bold text-blue-700">{m.code}</td>
                      <td className="py-2 px-3 font-medium text-slate-900">{m.name}</td>
                      <td className="py-2 px-3 text-center text-slate-600">{m.uom_default || '—'}</td>
                      <td className="py-2 px-3 text-center">
                        <span className="font-mono text-[10px] uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">
                          {m.group_code || '—'}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-right">
                        {isSelected ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700">
                            <Check className="h-3.5 w-3.5" /> Đã chọn
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation()
                              onSelect(m)
                            }}
                            className="inline-flex items-center gap-1 rounded bg-blue-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-blue-700 shadow-2xs"
                          >
                            Chọn
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 py-3 bg-slate-50 flex items-center justify-between">
          <span className="text-xs text-slate-500">
            Hiển thị {filteredMaterials.length} / {materials.length} vật tư
          </span>
          <button
            type="button"
            onClick={onClose}
            className="rounded bg-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-300 transition"
          >
            Đóng
          </button>
        </div>
      </div>
    </div>
  )
}
